import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { LocationSearchComponent } from '../location-search/location-search.component';

@Component({
  selector: 'app-mobile-location-dock',
  templateUrl: './mobile-location-dock.component.html',
  styleUrls: ['./mobile-location-dock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileLocationDockComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() open = false;
  @Input() selectedAddress: string | null = null;
  @Input() isClear = false;
  /**
   * Viewport rect of the trigger pill, captured by the parent on tap. It lets the field
   * expand from where the pill was instead of appearing from nowhere. Optional: without it
   * the field simply fades in.
   */
  @Input() originRect: { left: number; width: number } | null = null;

  @Output() locationSelected = new EventEmitter<{ lat: number; lng: number; address: string } | null>();
  @Output() locationInputChanged = new EventEmitter<string>();

  @ViewChild('dockLocationSearch') dockLocationSearch?: LocationSearchComponent;

  private viewportListenersAttached = false;
  private readonly updateViewportOffset = () => {
    this.setKeyboardOffset();
    this.setButtonClearance();
  };

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit() {
    // The clearance var also positions the page's active-filter row, so publish it even when
    // the field has never been opened; re-measure once after late layout (fonts, icons).
    this.setButtonClearance();
    setTimeout(() => this.setButtonClearance(), 400);

    if (this.open) {
      this.activate();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['open']) {
      return;
    }

    if (this.open) {
      this.activate();
      return;
    }

    // Listeners only live while the field is open — idle pages pay nothing.
    this.detachViewportListeners();
    this.setKeyboardOffset(0);
  }

  ngOnDestroy() {
    this.detachViewportListeners();
    this.setKeyboardOffset(0);
  }

  focusInput(selectAll = false) {
    this.cdr.detectChanges();
    this.dockLocationSearch?.focusInput(selectAll);

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => this.dockLocationSearch?.focusInput(selectAll));
    }

    setTimeout(() => this.dockLocationSearch?.focusInput(selectAll), 80);
    setTimeout(() => this.dockLocationSearch?.focusInput(selectAll), 240);
  }

  private activate() {
    this.attachViewportListeners();
    this.setKeyboardOffset();
    this.setButtonClearance();
    this.setOrigin();
    this.focusInput(true);
  }

  private attachViewportListeners() {
    if (this.viewportListenersAttached || typeof window === 'undefined') {
      return;
    }

    const viewport = (window as any).visualViewport;
    viewport?.addEventListener('resize', this.updateViewportOffset);
    viewport?.addEventListener('scroll', this.updateViewportOffset);
    window.addEventListener('resize', this.updateViewportOffset);
    this.viewportListenersAttached = true;
  }

  private detachViewportListeners() {
    if (!this.viewportListenersAttached || typeof window === 'undefined') {
      return;
    }

    const viewport = (window as any).visualViewport;
    viewport?.removeEventListener('resize', this.updateViewportOffset);
    viewport?.removeEventListener('scroll', this.updateViewportOffset);
    window.removeEventListener('resize', this.updateViewportOffset);
    this.viewportListenersAttached = false;
  }

  private setKeyboardOffset(forceOffset?: number) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const viewport = (window as any).visualViewport;
    const offset = forceOffset ?? (
      viewport
        ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
        : 0
    );

    document.documentElement.style.setProperty(
      '--hungery-mobile-keyboard-offset',
      `${Math.round(offset)}px`
    );
    this.cdr.markForCheck();
  }

  /**
   * How far the floating filter/search buttons sit above the bottom of their (transparent,
   * taller) bar. Measured rather than assumed: it differs between browsers and grows with the
   * iOS bottom safe-area inset, and the field has to clear the buttons on every device without
   * a hand-tuned magic number.
   */
  private setButtonClearance() {
    if (typeof document === 'undefined') {
      return;
    }

    const bar = document.querySelector('.hungery-mobile-search-wrap') as HTMLElement | null;
    const button = bar?.querySelector('.btn-filter, .btn-search') as HTMLElement | null;
    if (!bar || !button) {
      return;
    }

    const clearance = bar.getBoundingClientRect().bottom - button.getBoundingClientRect().top;
    if (clearance <= 0) {
      return;
    }

    document.documentElement.style.setProperty(
      '--hungery-mobile-filter-btn-clearance',
      `${Math.round(clearance)}px`
    );
  }

  private setOrigin() {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const root = document.documentElement;
    if (!this.originRect?.width) {
      root.style.removeProperty('--hungery-mobile-dock-origin-left');
      root.style.removeProperty('--hungery-mobile-dock-origin-right');
      return;
    }

    const { left, width } = this.originRect;
    root.style.setProperty('--hungery-mobile-dock-origin-left', `${Math.round(left)}px`);
    root.style.setProperty(
      '--hungery-mobile-dock-origin-right',
      `${Math.round(Math.max(0, window.innerWidth - left - width))}px`
    );
  }
}
